import { RingBuffer } from '../third_party/ringbufjs/ringbuf.module.js';

/**
 * Manages shared ring buffers for video frames
 */
export class BufferManager {
    constructor() {
        this.buffers = new Map();  // Map<bufferId, BufferInfo>
        this.consumers = new Map(); // Map<bufferId, Set<consumer>>
        this.pipelines = new Map(); // Map<bufferId, pipeline>
        this.stereoGroups = new Map(); // Map<groupId, Set<bufferId>>
    }

    /**
     * Parse stereo information from buffer ID
     * @param {string} bufferId Buffer identifier (format: video-{index}-{mask})
     * @returns {Object} Stereo information
     */
    parseStereoInfo(bufferId) {
        const parts = bufferId.split('-');
        return {
            index: parts[1],
            mask: parts[2] || 'mono',
            isStereo: parts[2] === 'left' || parts[2] === 'right'
        };
    }

    /**
     * Register a stereo buffer group
     * @param {string} bufferId Buffer identifier
     * @param {string} groupId Group identifier
     */
    registerStereoGroup(bufferId, groupId) {
        if (!this.stereoGroups.has(groupId)) {
            this.stereoGroups.set(groupId, new Set());
        }
        this.stereoGroups.get(groupId).add(bufferId);
    }

    /**
     * Create a new ring buffer
     * @param {string} bufferId Unique identifier for the buffer
     * @param {number} width Frame width
     * @param {number} height Frame height
     * @param {number} channels Number of channels (e.g., 4 for RGBA)
     * @param {number} maxFrames Maximum number of frames to store
     * @param {Function} componentType Array type (e.g., Uint8Array)
     * @returns {RingBuffer} The created ring buffer
     */
    createBuffer(bufferId, width, height, channels, maxFrames, componentType, groupId = null) {
        // Check if buffer already exists
        if (this.buffers.has(bufferId)) {
            console.log(`Buffer ${bufferId} already exists, returning existing buffer`);
            return this.buffers.get(bufferId).buffer;
        }

        // Parse stereo information from buffer ID
        const stereoInfo = this.parseStereoInfo(bufferId);
        console.log('Creating buffer with stereo info:', {
            bufferId,
            stereoInfo,
            groupId
        });

        // Validate stereo buffer creation
        if (stereoInfo.isStereo && !groupId) {
            console.warn(`Creating stereo buffer ${bufferId} without group ID`);
        }

        // Create the buffer
        const frameSize = width * height * channels;
        const totalSize = frameSize * maxFrames;
        
        try {
            const sab = RingBuffer.getStorageForCapacity(totalSize, componentType);
            const buffer = new RingBuffer(sab, componentType);
            console.log('Buffer created successfully:', {
                bufferId,
                frameSize,
                totalSize,
                maxFrames
            });

            // Register stereo group if applicable
            if (groupId) {
                this.registerStereoGroup(bufferId, groupId);
            }

            // Store buffer info
            this.buffers.set(bufferId, {
                buffer,
                stereoInfo,
                groupId,
                width,
                height,
                channels,
                frameSize,
                maxFrames,
                componentType
            });

            // Initialize consumer and pipeline sets
            this.consumers.set(bufferId, new Set());
            this.pipelines.set(bufferId, null);

            return buffer;
        } catch (error) {
            console.error('Failed to create buffer:', error);
            throw new Error(`Failed to create buffer ${bufferId}: ${error.message}`);
        }
    }

    /**
     * Get an existing buffer
     * @param {string} bufferId Buffer identifier
     * @returns {RingBuffer} The requested buffer
     */
    getBuffer(bufferId) {
        const bufferInfo = this.buffers.get(bufferId);
        if (!bufferInfo) {
            console.warn(`Buffer ${bufferId} not found, available buffers:`, 
                Array.from(this.buffers.keys()));
            return null;
        }
        return bufferInfo.buffer;
    }

    /**
     * Check if a buffer exists
     * @param {string} bufferId Buffer identifier
     * @returns {boolean} True if buffer exists
     */
    hasBuffer(bufferId) {
        return this.buffers.has(bufferId);
    }

    /**
     * Register a consumer for a buffer
     * @param {string} bufferId Buffer identifier
     * @param {Object} consumer Consumer object
     */
    registerConsumer(bufferId, consumer) {
        if (!this.consumers.has(bufferId)) {
            throw new Error(`Buffer ${bufferId} not found`);
        }
        this.consumers.get(bufferId).add(consumer);
    }

    /**
     * Register a pipeline for a buffer
     * @param {string} bufferId Buffer identifier
     * @param {Object} pipeline Pipeline object
     */
    registerPipeline(bufferId, pipeline) {
        if (!this.buffers.has(bufferId)) {
            throw new Error(`Buffer ${bufferId} not found`);
        }
        this.pipelines.set(bufferId, pipeline);
    }

    /**
     * Get buffer information
     * @param {string} bufferId Buffer identifier
     * @returns {Object} Buffer information
     */
    getBufferInfo(bufferId) {
        return this.buffers.get(bufferId);
    }

    /**
     * Get all consumers for a buffer
     * @param {string} bufferId Buffer identifier
     * @returns {Set} Set of consumers
     */
    getConsumers(bufferId) {
        return this.consumers.get(bufferId);
    }

    /**
     * Get the pipeline for a buffer
     * @param {string} bufferId Buffer identifier
     * @returns {Object} Pipeline object
     */
    getPipeline(bufferId) {
        return this.pipelines.get(bufferId);
    }

    /**
     * Remove a buffer and clean up its resources
     * @param {string} bufferId Buffer identifier
     */
    removeBuffer(bufferId) {
        const bufferInfo = this.buffers.get(bufferId);
        if (bufferInfo && bufferInfo.groupId) {
            // Remove from stereo group
            const group = this.stereoGroups.get(bufferInfo.groupId);
            if (group) {
                group.delete(bufferId);
                if (group.size === 0) {
                    this.stereoGroups.delete(bufferInfo.groupId);
                }
            }
        }
        this.buffers.delete(bufferId);
        this.consumers.delete(bufferId);
        this.pipelines.delete(bufferId);
    }

    /**
     * Get all buffers in a stereo group
     * @param {string} groupId Group identifier
     * @returns {Array<string>} Array of buffer IDs in the group
     */
    getStereoGroup(groupId) {
        const group = this.stereoGroups.get(groupId);
        return group ? Array.from(group) : [];
    }

    /**
     * Check if all buffers in a stereo group are ready
     * @param {string} groupId Group identifier
     * @returns {boolean} True if all buffers in group are ready
     */
    isStereoGroupReady(groupId) {
        const group = this.stereoGroups.get(groupId);
        if (!group) return false;

        for (const bufferId of group) {
            const bufferInfo = this.buffers.get(bufferId);
            if (!bufferInfo || !bufferInfo.buffer) return false;
        }
        return true;
    }

    /**
     * Get stereo pair buffer for a given buffer
     * @param {string} bufferId Buffer identifier
     * @returns {string|null} ID of the paired buffer or null if not found
     */
    getStereoPairBuffer(bufferId) {
        const bufferInfo = this.buffers.get(bufferId);
        if (!bufferInfo || !bufferInfo.groupId) return null;

        const group = this.stereoGroups.get(bufferInfo.groupId);
        if (!group) return null;

        // Find the other buffer in the pair
        for (const otherId of group) {
            if (otherId !== bufferId) {
                return otherId;
            }
        }
        return null;
    }
}

// Create singleton instance
export const bufferManager = new BufferManager();